import { useState, useEffect, useRef } from 'react'
import { useParams } from 'react-router-dom'
import { Camera, Search, Plus, CheckCircle, Apple, AlertTriangle, Barcode, X, ChevronLeft, ChevronRight, Edit2, Trash2 } from 'lucide-react'
import toast from 'react-hot-toast'
import axios from 'axios'
import api from '../../services/api'
import { Html5Qrcode } from 'html5-qrcode'

const MEAL_TIMES = {
    breakfast: '🌅 Petit-déjeuner',
    lunch: '☀️ Déjeuner',
    snack: '🍎 Goûter',
    dinner: '🌙 Dîner'
}

const FEELINGS =[
    { emoji: '😋', label: 'Super' },
    { emoji: '😐', label: 'Bof' },
    { emoji: '🤢', label: 'Lourd' },
    { emoji: '🤤', label: 'Faim' }
]

// Utilitaire pour colorer le Nutri-Score
const getNutriScoreColor = (grade) => {
    switch(grade?.toLowerCase()) {
        case 'a': return 'bg-green-600 text-white border-green-700';
        case 'b': return 'bg-green-400 text-white border-green-500';
        case 'c': return 'bg-yellow-400 text-gray-800 border-yellow-500';
        case 'd': return 'bg-orange-500 text-white border-orange-600';
        case 'e': return 'bg-red-600 text-white border-red-700';
        default: return 'hidden'; // Si pas de Nutri-Score, on cache la pastille
    }
}

export default function PortalCarnetPage() {
    const { token } = useParams()
    const[activeTab, setActiveTab] = useState('journal') 
    
    const [dietPlan, setDietPlan] = useState(null)
    const [meals, setMeals] = useState([])
    const [achievedGoals, setAchievedGoals] = useState([])
    const [loading, setLoading] = useState(true)

    // Formulaire d'ajout
    const [isAdding, setIsAdding] = useState(false)
    const [newMeal, setNewMeal] = useState({ meal_time: 'lunch', food_text: '', feeling: '', file: null })
    
    //  Formulaire d'édition "Inline"
    const[editingMealId, setEditingMealId] = useState(null)
    const [editMealData, setEditMealData] = useState({ food_text: '', feeling: '' })

    
    
    // OpenFoodFacts (Recherche Textuelle)
    const [offSearch, setOffSearch] = useState('')
    const[offResults, setOffResults] = useState([])
    const [searchingOff, setSearchingOff] = useState(false)

    // OpenFoodFacts (Scanner Code-Barres)
    const[isScanning, setIsScanning] = useState(false)
    const scannerRef = useRef(null)

    const today = new Date().toISOString().split('T')[0]
    const [currentDate, setCurrentDate] = useState(today) // <-- Date active

    // Fonction sécurisée pour changer de jour
    const changeDate = (days) => {
        const d = new Date(currentDate)
        d.setDate(d.getDate() + days)
        setCurrentDate(d.toISOString().split('T')[0])
    }

    // 1. Initialisation des données
    useEffect(() => {
        fetchData()
        return () => stopScanner()
    },[token, currentDate]) // <-- On recharge si la date change


    // 2. LE DEBOUNCE MAGIQUE (Protège l'API OpenFoodFacts)
    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            if (offSearch.length >= 3) {
                searchOpenFoodFacts(offSearch)
            } else {
                setOffResults([])
            }
        }, 800) // Attend 800ms de "silence clavier" avant de lancer la recherche

        return () => clearTimeout(delayDebounceFn)
    },[offSearch])

    const toggleGoal = async (axe) => {
        // Optimistic UI (On met à jour l'écran tout de suite pour la fluidité)
        const newGoals = achievedGoals.includes(axe) ? achievedGoals.filter(a => a !== axe) :[...achievedGoals, axe];
        setAchievedGoals(newGoals);
        
        try {
            // Sauvegarde en arrière-plan
            await api.post(`/api/portal/${token}/daily-goals`, { date: currentDate, axes: newGoals });
        } catch (e) {
            toast.error("Erreur réseau");
            // Rollback en cas d'erreur
            setAchievedGoals(achievedGoals);
        }
    }

    const fetchData = async () => {
        try {
            const[planRes, mealsRes, goalsRes] = await Promise.all([
                api.get(`/api/portal/${token}/diet-plan`),
                api.get(`/api/portal/${token}/meals?date=${currentDate}`), // <-- Utilise la date active
                api.get(`/api/portal/${token}/daily-goals?date=${currentDate}`)
            ])

            setDietPlan(planRes.data)
            setMeals(mealsRes.data)
            setAchievedGoals(goalsRes.data.achieved_axes ||[]) 
        } catch (e) {
            toast.error("Erreur de connexion")
        } finally {
            setLoading(false)
        }
    }

    // --- RECHERCHE TEXTUELLE (ENVIRONNEMENT STAGING .NET) ---
    const searchOpenFoodFacts = async (query) => {
        setSearchingOff(true)
        try {
            // On utilise l'environnement .net pour les tests, et on demande explicitement le nutriscore_grade
            const res = await axios.get(`https://world.openfoodfacts.net/cgi/search.pl?search_terms=${encodeURIComponent(query)}&search_simple=1&action=process&json=1&page_size=5&fields=code,product_name,image_front_thumb_url,nutriments,nutriscore_grade`)
            setOffResults(res.data.products ||[])
        } catch (e) { 
            console.error("Erreur OFF:", e) 
        } finally { 
            setSearchingOff(false) 
        }
    }

    // --- SCANNER CODE-BARRES (ENVIRONNEMENT STAGING .NET) ---
    const startScanner = () => {
        setIsScanning(true)
        setTimeout(() => {
            scannerRef.current = new Html5Qrcode("reader")
            scannerRef.current.start(
                { facingMode: "environment" },
                { fps: 10, qrbox: { width: 250, height: 150 } },
                (decodedText) => {
                    stopScanner()
                    fetchProductByBarcode(decodedText)
                },
                (err) => { /* Ignore les frames illisibles */ }
            ).catch(err => {
                toast.error("Impossible d'accéder à la caméra.")
                setIsScanning(false)
            })
        }, 200)
    }

    const stopScanner = () => {
        if (scannerRef.current) {
            scannerRef.current.stop().then(() => {
                scannerRef.current.clear()
                setIsScanning(false)
            }).catch(() => setIsScanning(false))
        } else {
            setIsScanning(false)
        }
    }

    const fetchProductByBarcode = async (barcode) => {
        const toastId = toast.loading("Recherche du produit...")
        try {
            // On utilise l'API V3 sur l'environnement .net
            const res = await axios.get(`https://world.openfoodfacts.net/api/v3/product/${barcode}.json?fields=code,product_name,image_front_thumb_url,nutriments,nutriscore_grade`)
            if (res.data.product) {
                const prod = res.data.product
                const kcal = prod.nutriments?.['energy-kcal_100g'] ? ` (${Math.round(prod.nutriments['energy-kcal_100g'])} kcal/100g)` : ''
                const text = `• ${prod.product_name || 'Produit inconnu'}${kcal}`
                
                setNewMeal(prev => ({...prev, food_text: prev.food_text ? prev.food_text + '\n' + text : text}))
                toast.success("Produit scanné et ajouté !", { id: toastId })
            } else {
                toast.error("Produit introuvable", { id: toastId })
            }
        } catch (e) {
            toast.error("Erreur réseau", { id: toastId })
        }
    }

    // --- SAUVEGARDE DU REPAS ---
    const handleAddMeal = async (e) => {
        e.preventDefault()
        if (!newMeal.food_text) return toast.error("Veuillez indiquer ce que vous avez mangé.")

        const formData = new FormData()
        formData.append('date', currentDate) // <-- sauvegarde la date active
        formData.append('meal_time', newMeal.meal_time)
        formData.append('food_text', newMeal.food_text)
        if (newMeal.feeling) formData.append('feeling', newMeal.feeling)
        if (newMeal.file) formData.append('file', newMeal.file)

        toast.loading("Enregistrement...", { id: 'saveMeal' })
        try {
            await api.post(`/api/portal/${token}/meals`, formData, { headers: { 'Content-Type': 'multipart/form-data' } })
            toast.success("Repas enregistré !", { id: 'saveMeal' })
            setIsAdding(false)
            setNewMeal({ meal_time: 'lunch', food_text: '', feeling: '', file: null })
            setOffSearch('')
            setOffResults([])
            fetchData()
        } catch (e) {
            toast.error("Erreur d'enregistrement", { id: 'saveMeal' })
        }
    }

    const handleDeleteMeal = async (id) => {
        if (!confirm("Voulez-vous vraiment supprimer ce repas ?")) return;
        try {
            await api.delete(`/api/portal/${token}/meals/${id}`);
            toast.success("Repas supprimé !");
            fetchData();
        } catch (e) { toast.error("Erreur lors de la suppression"); }
    }

    const startEditMeal = (meal) => {
        setEditingMealId(meal.id);
        setEditMealData({ 
            meal_time: meal.meal_time,
            food_text: meal.food_text, 
            feeling: meal.feeling || '' 
        });
    }


    const handleUpdateMeal = async (id) => {
        try {
            await api.patch(`/api/portal/${token}/meals/${id}`, editMealData);
            toast.success("Repas modifié !");
            setEditingMealId(null);
            fetchData();
        } catch (e) { toast.error("Erreur lors de la modification"); }
    }


    if (loading) return <div className="flex justify-center py-20"><div className="spinner border-sage-600"/></div>

    return (
        <div className="space-y-6 animate-in fade-in pb-20">
            {/* Header */}
            <div>
                <h1 className="text-2xl font-serif text-sage-800">Votre Carnet 🍏</h1>
                <p className="text-gray-500 text-sm">Le suivi quotidien de votre alimentation.</p>
            </div>

            {/* Onglets */}
            <div className="flex bg-gray-100 p-1 rounded-xl">
                <button onClick={() => setActiveTab('journal')} className={`flex-1 py-2 rounded-lg text-sm font-bold transition-all ${activeTab === 'journal' ? 'bg-white shadow-sm text-sage-700' : 'text-gray-500'}`}>
                    Journal
                </button>
                <button onClick={() => setActiveTab('plan')} className={`flex-1 py-2 rounded-lg text-sm font-bold transition-all ${activeTab === 'plan' ? 'bg-white shadow-sm text-sage-700' : 'text-gray-500'}`}>
                    Mon Plan
                </button>
            </div>

            {/* VUE : MON JOURNAL */}
            {activeTab === 'journal' && (
                <div className="space-y-6">

                    {/* BARRE DE NAVIGATION DES JOURS */}
                    <div className="flex justify-between items-center bg-white p-2 rounded-2xl shadow-sm border border-gray-100">
                        <button onClick={() => changeDate(-1)} className="p-2 hover:bg-gray-50 rounded-xl text-gray-500 transition-colors">
                            <ChevronLeft className="w-5 h-5" />
                        </button>
                        <div className="font-bold text-sage-700 text-sm capitalize flex flex-col items-center">
                            {currentDate === today ? "Aujourd'hui" : new Date(currentDate).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'short' })}
                        </div>
                        <button 
                            onClick={() => changeDate(1)} 
                            disabled={currentDate === today} 
                            className={`p-2 rounded-xl transition-colors ${currentDate === today ? 'text-gray-200' : 'text-gray-500 hover:bg-gray-50'}`}
                        >
                            <ChevronRight className="w-5 h-5" />
                        </button>
                    </div>
                    
                    {/* Les Axes (Gamification) DEPLACES ICI ! */}
                    {dietPlan?.axes && dietPlan.axes.length > 0 && (
                        <div className="bg-orange-50 border border-orange-100 p-5 rounded-2xl shadow-sm">
                            <h3 className="text-xs font-bold text-orange-800 uppercase mb-3">Mes objectifs du jour 🎯</h3>
                            <div className="space-y-2">
                                {dietPlan.axes.map((axe, idx) => (
                                    <label key={idx} className="flex items-center gap-3 p-3 bg-white rounded-xl border border-orange-100 shadow-sm cursor-pointer hover:bg-orange-50/50 transition-colors">
                                        <input 
                                            type="checkbox" 
                                            checked={achievedGoals.includes(axe)} 
                                            onChange={() => toggleGoal(axe)} 
                                            className="w-5 h-5 rounded border-orange-300 text-orange-500 focus:ring-orange-500" 
                                        />
                                        <span className={`text-sm font-medium leading-tight transition-all ${achievedGoals.includes(axe) ? 'text-gray-400 line-through' : 'text-gray-700'}`}>{axe}</span>
                                    </label>
                                ))}
                            </div>
                        </div>
                    )}

                    <div className="space-y-4">
                        {meals.length === 0 && !isAdding && (
                            <div className="text-center p-8 border-2 border-dashed border-sage-200 bg-sage-50 rounded-2xl">
                                <Apple className="w-12 h-12 text-sage-300 mx-auto mb-3" />
                                <p className="text-sage-600 font-medium">Vous n'avez encore rien noté aujourd'hui.</p>
                            </div>
                        )}
                        
                        {meals.map(meal => (
                            <div key={meal.id} className="bg-white p-4 rounded-2xl shadow-sm border border-gray-100 flex gap-4">
                                {meal.image_url && editingMealId !== meal.id && (
                                    <img src={meal.image_url} alt="Repas" className="w-20 h-20 object-cover rounded-xl shadow-sm" />
                                )}
                                <div className="flex-1 min-w-0">
                                    {/* MODE ÉDITION */}
                                    {editingMealId === meal.id ? (
                                        <div className="flex flex-col gap-3 animate-in fade-in">
                                            
                                            {/* Sélecteur du moment du repas */}
                                            <select 
                                                value={editMealData.meal_time} 
                                                onChange={e => setEditMealData({...editMealData, meal_time: e.target.value})} 
                                                className="w-full bg-gray-50 border-gray-200 rounded-xl py-2 px-3 text-sm font-bold text-gray-700 focus:ring-sage-300"
                                            >
                                                <option value="breakfast">Matin</option>
                                                <option value="lunch">Midi</option>
                                                <option value="snack">Goûter</option>
                                                <option value="dinner">Soir</option>
                                            </select>

                                            <textarea 
                                                value={editMealData.food_text}
                                                onChange={(e) => setEditMealData({...editMealData, food_text: e.target.value})}
                                                className="w-full bg-gray-50 border-gray-200 rounded-xl p-3 text-sm resize-none focus:ring-sage-300"
                                                rows="3"
                                            />
                                            <div className="flex justify-between gap-2">
                                                {FEELINGS.map(f => (
                                                    <button 
                                                        key={f.emoji} type="button"
                                                        onClick={() => setEditMealData({...editMealData, feeling: f.emoji})}
                                                        className={`flex-1 flex flex-col items-center p-2 rounded-xl border-2 transition-all ${editMealData.feeling === f.emoji ? 'border-sage-400 bg-sage-50' : 'border-gray-100 bg-white grayscale opacity-50 hover:grayscale-0 hover:opacity-100'}`}
                                                    >
                                                        <span className="text-xl mb-1">{f.emoji}</span>
                                                    </button>
                                                ))}
                                            </div>
                                            <div className="flex gap-2 mt-1">
                                                <button onClick={() => setEditingMealId(null)} className="flex-1 btn bg-gray-100 text-gray-600 py-2 text-sm rounded-xl">Annuler</button>
                                                <button onClick={() => handleUpdateMeal(meal.id)} className="flex-1 btn btn-primary py-2 text-sm rounded-xl">Valider</button>
                                            </div>
                                        </div>
                                    ) : (
                                        /* MODE LECTURE */
                                        <>
                                            <div className="flex justify-between items-start mb-1">
                                                <span className="text-[10px] font-bold text-sage-600 uppercase bg-sage-50 px-2 py-0.5 rounded">
                                                    {MEAL_TIMES[meal.meal_time]}
                                                </span>
                                                <div className="flex items-center gap-2">
                                                    {meal.feeling && <span className="text-xl flex-shrink-0">{meal.feeling}</span>}
                                                    <div className="flex gap-1 ml-2 border-l border-gray-100 pl-2">
                                                        <button onClick={() => startEditMeal(meal)} className="p-1.5 text-gray-400 hover:text-sage-600 hover:bg-sage-50 rounded-lg transition-colors"><Edit2 className="w-3.5 h-3.5"/></button>
                                                        <button onClick={() => handleDeleteMeal(meal.id)} className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"><Trash2 className="w-3.5 h-3.5"/></button>
                                                    </div>
                                                </div>
                                            </div>
                                            <p className="text-sm text-gray-800 font-medium whitespace-pre-wrap mt-2">{meal.food_text}</p>
                                        </>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>

                    {!isAdding ? (
                        <button onClick={() => setIsAdding(true)} className="w-full btn bg-sage-600 text-white hover:bg-sage-700 shadow-xl shadow-sage-200/50 flex items-center justify-center gap-2 py-4 rounded-2xl">
                            <Plus className="w-5 h-5"/> Noter un repas
                        </button>
                    ) : (
                        <div className="bg-white rounded-2xl p-5 shadow-xl border border-sage-200 animate-in slide-in-from-bottom-4">
                            <h3 className="font-bold text-sage-800 mb-4">Nouveau Repas</h3>
                            <form onSubmit={handleAddMeal} className="space-y-4">
                                <select id="meal_time" name="meal_time" aria-label="Moment du repas" value={newMeal.meal_time} onChange={e => setNewMeal({...newMeal, meal_time: e.target.value})} className="w-full bg-gray-50 border-none focus:ring-sage-300 rounded-xl py-3 font-bold text-gray-700">
                                    <option value="breakfast">Matin</option>
                                    <option value="lunch">Midi</option>
                                    <option value="snack">Goûter</option>
                                    <option value="dinner">Soir</option>
                                </select>

                                {/* Bloc Photo (Image de l'assiette) */}
                                <label className="flex flex-col items-center justify-center w-full h-24 border-2 border-dashed border-sage-200 bg-sage-50 hover:bg-sage-100 rounded-xl cursor-pointer transition-colors relative overflow-hidden">
                                    {newMeal.file ? (
                                        <img src={URL.createObjectURL(newMeal.file)} alt="Preview" className="w-full h-full object-cover opacity-60" />
                                    ) : (
                                        <>
                                            <Camera className="w-6 h-6 text-sage-500 mb-1" />
                                            <span className="text-xs font-bold text-sage-600">Photo de l'assiette</span>
                                        </>
                                    )}
                                    <input type="file" accept="image/*" capture="environment" onChange={e => setNewMeal({...newMeal, file: e.target.files[0]})} className="hidden" />
                                </label>

                                 {/* Bloc OpenFoodFacts */}
                                <div className="bg-blue-50/50 border border-blue-100 p-3 rounded-xl">
                                    <label htmlFor="off_search" className="text-[10px] font-bold text-blue-800 uppercase mb-2 block">Assistant Produit (OpenFoodFacts)</label>
                                    <div className="flex gap-2">
                                        <div className="relative flex-1">
                                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-blue-400" />
                                            <input 
                                                id="off_search"
                                                name="off_search"
                                                placeholder="Rechercher (ex: Pâtes Barilla)" 
                                                value={offSearch}
                                                onChange={e => setOffSearch(e.target.value)} // Le useEffect gère l'appel API
                                                className="w-full pl-9 bg-white border-blue-200 text-sm rounded-xl py-2.5 focus:ring-blue-300"
                                            />
                                            {searchingOff && <div className="absolute right-3 top-1/2 -translate-y-1/2 spinner w-4 h-4 border-blue-500" />}
                                            
                                            {/* Liste de résultats avec NUTRI-SCORE */}
                                            {offResults.length > 0 && (
                                                <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-2xl overflow-hidden z-50">
                                                    {offResults.map(prod => (
                                                        <div 
                                                            key={prod.code}  
                                                            onClick={() => {
                                                                const kcal = prod.nutriments?.['energy-kcal_100g'] ? ` (${Math.round(prod.nutriments['energy-kcal_100g'])} kcal/100g)` : '';
                                                                const text = `• ${prod.product_name || 'Produit'}${kcal}`;
                                                                setNewMeal({...newMeal, food_text: newMeal.food_text ? newMeal.food_text + '\n' + text : text});
                                                                setOffResults([]); setOffSearch('');
                                                            }}
                                                            className="px-3 py-2 text-xs border-b border-gray-100 hover:bg-blue-50 cursor-pointer flex items-center gap-3"
                                                        >
                                                            {prod.image_front_thumb_url ? <img src={prod.image_front_thumb_url} className="w-8 h-8 object-cover rounded bg-gray-100 shrink-0" alt="Produit" /> : <div className="w-8 h-8 bg-gray-100 rounded shrink-0" />}
                                                            
                                                            <div className="flex-1 flex flex-col justify-center min-w-0">
                                                                <div className="truncate font-bold text-gray-700">{prod.product_name}</div>
                                                                <div className="flex items-center gap-2 mt-0.5">
                                                                    <div className="text-sage-600 font-mono text-[10px]">{prod.nutriments?.['energy-kcal_100g'] ? `${Math.round(prod.nutriments['energy-kcal_100g'])} kcal` : ''}</div>
                                                                    
                                                                    {/* Pastille NutriScore */}
                                                                    <span className={`text-[9px] font-black px-1.5 py-0.5 border rounded-sm ${getNutriScoreColor(prod.nutriscore_grade)}`}>
                                                                        {prod.nutriscore_grade ? `Nutri-Score ${prod.nutriscore_grade.toUpperCase()}` : ''}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>

                                        <button 
                                            type="button" 
                                            onClick={startScanner}
                                            className="btn bg-blue-600 text-white px-4 rounded-xl flex items-center justify-center shadow-md hover:bg-blue-700"
                                            title="Scanner un code-barres"
                                        >
                                            <Barcode className="w-5 h-5" />
                                        </button>
                                    </div>
                                </div>

                                <textarea 
                                    id="food_text"
                                    name="food_text"
                                    aria-label="Ce que j'ai mangé"
                                    rows="4" 
                                    required
                                    value={newMeal.food_text}
                                    onChange={e => setNewMeal({...newMeal, food_text: e.target.value})}
                                    placeholder="Ce que j'ai mangé en détails (ou ajoutez via l'assistant ci-dessus)..."
                                    className="w-full bg-gray-50 border-gray-200 focus:bg-white focus:ring-sage-300 text-sm resize-none rounded-xl p-4"
                                />

                                <div>
                                    <label htmlFor="feeling" className="text-[10px] font-bold text-gray-400 uppercase mb-2 block">Mon ressenti / Digestion</label>
                                    <div className="flex justify-between gap-2">
                                        {FEELINGS.map(f => (
                                            <button 
                                                key={f.emoji} type="button"
                                                onClick={() => setNewMeal({...newMeal, feeling: f.emoji})}
                                                className={`flex-1 flex flex-col items-center p-3 rounded-xl border-2 transition-all ${newMeal.feeling === f.emoji ? 'border-sage-400 bg-sage-50' : 'border-gray-100 bg-white grayscale opacity-50 hover:grayscale-0 hover:opacity-100'}`}
                                            >
                                                <span className="text-2xl mb-1">{f.emoji}</span>
                                                <span className="text-[9px] font-bold text-gray-600 uppercase text-center leading-tight">{f.label}</span>
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                <div className="flex gap-3 pt-2">
                                    <button type="button" onClick={() => setIsAdding(false)} className="flex-1 btn bg-gray-100 text-gray-600 rounded-xl py-3">Annuler</button>
                                    <button type="submit" className="flex-1 btn btn-primary rounded-xl py-3">Enregistrer</button>
                                </div>
                            </form>
                        </div>
                    )}
                </div>
            )}

            {/* VUE : MON PLAN ALIMENTAIRE */}
            {activeTab === 'plan' && (
                <div className="space-y-6">
                    {!dietPlan ? (
                        <div className="text-center p-8 border border-dashed border-gray-200 rounded-2xl bg-white">
                            <AlertTriangle className="w-8 h-8 text-orange-400 mx-auto mb-3" />
                            <p className="text-sm text-gray-500">Aucun plan alimentaire défini par votre praticien pour le moment.</p>
                        </div>
                    ) : (
                        <>
                            <div className="bg-sage-600 text-white p-5 rounded-2xl shadow-lg">
                                <h2 className="font-serif text-xl font-bold mb-1">{dietPlan.name}</h2>
                                <p className="text-sm opacity-80">Votre programme personnalisé</p>
                            </div>

                            {/* Les Repas du Plan */}
                            <div className="space-y-4">
                                {['breakfast', 'lunch', 'snack', 'dinner'].map(mealKey => {
                                    const items = dietPlan.items.filter(i => i.meal_time === mealKey)
                                    if(items.length === 0) return null;
                                    
                                    return (
                                        <div key={mealKey} className="bg-white border border-gray-100 rounded-2xl overflow-hidden shadow-sm">
                                            <div className="bg-gray-50 px-4 py-2 border-b border-gray-100">
                                                <h4 className="font-bold text-gray-700 text-sm">{MEAL_TIMES[mealKey]}</h4>
                                            </div>
                                            <div className="p-4 space-y-3">
                                                {items.map((item, idx) => (
                                                    <div key={idx} className="flex items-start gap-2">
                                                        <CheckCircle className="w-4 h-4 text-sage-400 mt-0.5 shrink-0" />
                                                        <div>
                                                            <p className="text-sm font-bold text-gray-800">{item.food_name}</p>
                                                            {(item.quantity || item.weight_g) && (
                                                                <p className="text-xs text-gray-500 mt-0.5">
                                                                    {item.quantity} {item.quantity && item.weight_g ? '-' : ''} {item.weight_g ? `${item.weight_g}g` : ''}
                                                                </p>
                                                            )}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )
                                })}
                            </div>
                        </>
                    )}
                </div>
            )}

            {/* --- MODALE FULLSCREEN SCANNER CODE-BARRES --- */}
            {isScanning && (
                <div className="fixed inset-0 bg-black z-[100] flex flex-col">
                    <div className="p-4 flex justify-between items-center bg-black/50 absolute top-0 w-full z-10">
                        <h3 className="text-white font-bold">Scannez un code-barres</h3>
                        <button onClick={stopScanner} className="bg-white/20 p-2 rounded-full text-white backdrop-blur-md">
                            <X className="w-6 h-6" />
                        </button>
                    </div>
                    {/* Le conteneur vidéo utilisé par html5-qrcode */}
                    <div id="reader" className="w-full flex-1 flex items-center justify-center bg-black"></div>
                </div>
            )}
        </div>
    )
}